import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Rocket, Globe, MessageSquare, CreditCard, Users, CheckCircle2,
  Building2, HardDrive
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function Launchpad() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // User-scoped localStorage key - prevents cross-account progress bleeding
  const launchpadKey = user?.id ? `launchpad_profile_completed_${user.id}` : null;
  const gdriveKey = user?.id ? `gdrive_connected_${user.id}` : "gdrive_connected";
  const [tasks, setTasks] = useState([
    {
      id: "business_profile",
      title: "Complete Business Profile & Address",
      sub: "Configure your legal company name, support phone number, timezone, and operational address",
      icon: Building2,
      done: false,
      cta: "Setup Profile",
      path: "/settings",
    },
    {
      id: "gbp",
      title: "Connect Google Business Profile",
      sub: "Generate inbound phone leads & 5-star reviews from local search",
      icon: Globe,
      done: false,
      cta: "Connect Google",
      path: "/marketplace",
    },
    {
      id: "gdrive",
      title: "Connect Google Drive to Media Storage",
      sub: "Sync brand assets, videos, client photos, and contracts directly into your media library",
      icon: HardDrive,
      done: false,
      cta: "Connect Drive",
      path: "/media",
    },
    {
      id: "social",
      title: "Connect Facebook & Instagram Accounts",
      sub: "Publish scheduled campaigns and auto-reply to client direct messages",
      icon: MessageSquare,
      done: false,
      cta: "Connect Meta",
      path: "/marketplace",
    },
    {
      id: "chat",
      title: "Install Web Chat Widget on Your Website",
      sub: "Convert visitors into qualified leads with an AI chat bubble and SMS lead capture",
      icon: MessageSquare,
      done: false,
      cta: "Get Chat Code",
      path: "/sites?tab=chat",
    },
    {
      id: "stripe",
      title: "Connect Stripe for Instant Client Payments",
      sub: "Collect credit cards, issue automated invoices, and charge subscriptions",
      icon: CreditCard,
      done: false,
      cta: "Connect Stripe",
      path: "/marketplace",
    },
    {
      id: "team",
      title: "Add Sales & Marketing Team Members",
      sub: "Invite colleagues with granular role-based permissions (Admin, Agent, Specialist)",
      icon: Users,
      done: false,
      cta: "Invite Team",
      path: "/settings?tab=team",
    },
  ]);

  useEffect(() => {
    // Check integrations and profile status
    Promise.all([
      api.get("/integrations").catch(() => ({ data: {} })),
      api.get("/auth/me").catch(() => ({ data: {} })),
    ]).then(([integRes, userRes]) => {
      const data = integRes.data;
      const user = userRes.data;

      const gdriveConnected = localStorage.getItem(gdriveKey) === "true";
      const profileDone = !!(user?.name && user?.phone);

      setTasks(prev => {
        const updated = prev.map(t => {
          if (t.id === "business_profile") return { ...t, done: profileDone };
          if (t.id === "gbp") return { ...t, done: !!data?.google?.connected };
          if (t.id === "gdrive") return { ...t, done: gdriveConnected };
          if (t.id === "social") return { ...t, done: !!data?.meta?.connected };
          if (t.id === "stripe") return { ...t, done: !!data?.stripe?.connected };
          return t;
        });

        const allDone = updated.every(x => x.done);
        if (allDone && launchpadKey) {
          localStorage.setItem(launchpadKey, "true");
        }
        return updated;
      });
    });
  }, []);

  const toggleTask = (id) => {
    setTasks(prev => {
      const next = prev.map(t => t.id === id ? { ...t, done: !t.done } : t);
      const allDone = next.every(x => x.done);
      if (allDone) {
        if (launchpadKey) localStorage.setItem(launchpadKey, "true");
        toast.success("All launchpad tasks complete! Profile 100% configured.");
      } else {
        if (launchpadKey) localStorage.removeItem(launchpadKey);
        toast.success("Progress updated");
      }
      return next;
    });
  };

  const completed = tasks.filter(t => t.done).length;

  return (
    <div className="p-6 sm:p-8 lg:p-10 space-y-6 max-w-[1400px] mx-auto w-full" data-testid="launchpad-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-950/40 text-blue-600 mb-2">
            <Rocket className="w-3.5 h-3.5" />
            <span>Launchpad Onboarding</span>
          </div>
          <h2 className="text-3xl font-black tracking-tight font-heading" style={{ color: "var(--text)" }}>
            Welcome to LaunchDeck
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>
            Complete the core real-world business setup steps below to activate your agency growth engine.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs text-slate-500 font-medium">Completion</div>
            <div className="text-lg font-black text-blue-600">{Math.round((completed / tasks.length) * 100)}%</div>
          </div>
          <div className="w-24 bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden">
            <div className="bg-blue-600 h-full rounded-full transition-all duration-500" style={{ width: `${(completed / tasks.length) * 100}%` }} />
          </div>
        </div>
      </div>

      {/* Task List */}
      <div className="grid grid-cols-1 gap-4">
        {tasks.map((task) => {
          const Icon = task.icon;
          return (
            <Card
              key={task.id}
              className={`p-5 border transition-all duration-150 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                task.done ? "opacity-90" : "shadow-xs hover:border-blue-400"
              }`}
              style={{ background: "var(--surface)", borderColor: "var(--border)" }}
            >
              <div className="flex items-start gap-4">
                <button
                  onClick={() => toggleTask(task.id)}
                  className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                    task.done ? "bg-emerald-500 text-white" : "border-2 border-slate-300 dark:border-slate-700 hover:border-blue-500"
                  }`}
                >
                  {task.done && <CheckCircle2 className="w-4 h-4 fill-emerald-500 text-white" />}
                </button>
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold text-sm sm:text-base flex items-center gap-2" style={{ color: "var(--text)" }}>
                    <span>{task.title}</span>
                    {task.done && <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">Completed</Badge>}
                  </h4>
                  <p className="text-xs sm:text-sm mt-0.5 text-slate-500">{task.sub}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 self-end sm:self-center">
                <Button
                  size="sm"
                  variant={task.done ? "outline" : "default"}
                  className={!task.done ? "bg-blue-600 hover:bg-blue-700 text-white font-semibold" : "text-xs"}
                  onClick={() => {
                    if (task.path) navigate(task.path);
                  }}
                >
                  {task.cta}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
